import React, { useState } from 'react';
import { paymentApi } from '../services/api';
import { Lock, NavArrowRight } from 'iconoir-react';
import '../styles/payment.css';

interface PaymentFormProps {
  amount: number;
  currency?: string;
  description?: string;
  planId: number;
  billingPeriod: 'monthly' | 'yearly';
  onPaymentSuccess: (paymentId: string) => void;
  onPaymentError: (error: string) => void;
}

/**
 * Stancer Hosted Payment Page (C04).
 * Le composant n'affiche plus de champs de carte : c'est Stancer qui héberge la page
 * de saisie, ce qui évite tout PCI-DSS côté Ventilio. Au clic sur "Payer", on demande
 * au backend une URL de paiement hosted puis on redirige le navigateur vers Stancer.
 * Au retour, Stancer renvoie l'utilisateur sur /payment/callback?payment_id=... qui
 * est géré par PaymentCallbackPage (vérification du statut côté API).
 */
export const PaymentForm: React.FC<PaymentFormProps> = ({
  amount,
  currency = 'EUR',
  description = 'Abonnement Ventilio',
  planId,
  billingPeriod,
  onPaymentSuccess: _onPaymentSuccess,
  onPaymentError,
}) => {
  const [loading, setLoading] = useState(false);

  const handlePayClick = async () => {
    setLoading(true);
    try {
      const response = await paymentApi.hostedCreate({
        amount,
        currency,
        description,
        plan_id: planId,
        billing_period: billingPeriod,
      });
      // Le browser quitte la SPA et part chez Stancer. Au retour : /payment/callback
      window.location.href = response.data.payment_page_url;
    } catch (error: any) {
      const message =
        error.response?.data?.message ||
        error.response?.data?.error ||
        'Erreur lors de la création du paiement';
      onPaymentError(message);
      setLoading(false);
    }
  };

  return (
    <div className="payment-form payment-form-hosted">
      <div className="payment-hosted-summary">
        <p>
          Vous allez être redirigé vers la page sécurisée de paiement <strong>Stancer</strong> pour
          saisir vos informations bancaires. Vos données de carte ne transitent jamais par Ventilio.
        </p>
      </div>

      <div className="payment-security">
        <Lock width={16} height={16} />
        <span>Paiement sécurisé par Stancer — connexion chiffrée TLS, conforme PCI-DSS</span>
      </div>

      <button
        type="button"
        className="btn btn-primary btn-payment"
        onClick={handlePayClick}
        disabled={loading}
      >
        {loading ? 'Redirection en cours…' : (
          <>
            Payer {amount.toFixed(2)}€
            <NavArrowRight width={18} height={18} strokeWidth={2} />
          </>
        )}
      </button>
    </div>
  );
};
